import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Coffee, LayoutDashboard, Receipt, ShoppingBasket, Settings, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";

const navItems = [
  { to: "/dashboard", label: "Dashboard", Icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/sales", label: "Sales", Icon: Receipt, testid: "nav-sales" },
  { to: "/expenses", label: "Expenses", Icon: ShoppingBasket, testid: "nav-expenses" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-taupe-100">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-taupe-200 bg-white">
        <div className="px-6 py-6 flex items-center gap-3 border-b border-taupe-200">
          <div className="w-10 h-10 rounded-xl bg-espresso-800 flex items-center justify-center text-white">
            <Coffee className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-espresso-900 text-lg tracking-tight leading-none">Brew Field</div>
            <div className="text-xs text-taupe-500 mt-1">Cafe Dashboard</div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(({ to, label, Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                  isActive
                    ? "bg-espresso-800 text-white shadow-sm"
                    : "text-espresso-800 hover:bg-taupe-100"
                }`
              }
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}

          {user?.role === "admin" && (
            <NavLink
              to="/settings/users"
              data-testid="nav-settings"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                  isActive || window.location.pathname.startsWith("/settings")
                    ? "bg-espresso-800 text-white shadow-sm"
                    : "text-espresso-800 hover:bg-taupe-100"
                }`
              }
            >
              <Settings className="w-5 h-5" />
              Settings
            </NavLink>
          )}
        </nav>

        <div className="p-4 border-t border-taupe-200">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold text-espresso-900" data-testid="current-user-name">
              {user?.name || user?.email}
            </div>
            <div className="text-xs text-taupe-500 capitalize">{user?.role}</div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full rounded-xl border-taupe-200 hover:border-espresso-800 hover:bg-taupe-100 text-espresso-800 font-semibold"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-20 bg-white border-b border-taupe-200 flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <Coffee className="w-5 h-5 text-espresso-800" />
          <span className="font-heading font-bold text-espresso-900">BrewField</span>
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          size="sm"
          className="text-espresso-800"
          data-testid="logout-button-mobile"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-taupe-200 flex justify-around z-20">
          {navItems.map(({ to, label, Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex-1 py-3 text-center text-xs font-semibold ${
                  isActive ? "text-espresso-900" : "text-taupe-500"
                }`
              }
            >
              <Icon className="w-5 h-5 mx-auto mb-1" />
              {label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/settings/users"
              data-testid="nav-settings-mobile"
              className={({ isActive }) =>
                `flex-1 py-3 text-center text-xs font-semibold ${
                  isActive ? "text-espresso-900" : "text-taupe-500"
                }`
              }
            >
              <Settings className="w-5 h-5 mx-auto mb-1" />
              Settings
            </NavLink>
          )}
        </nav>

        <div className="pb-20 md:pb-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
